import { ArrowUp, Camera, Sparkle, WarningCircle, X } from "@phosphor-icons/react";
import { ChevronDown } from "lucide-react";
import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { getApiErrorMessage, useAskAssistant, useRecordIssue } from "@/lib/api";
import { downscaleToJpeg, pickImage } from "@/lib/image-capture";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { AssistantContext, type OpenOptions, type SkipStepHandler } from "./assistant-context";
import { isNewPhase, suggestionsForStep } from "./assistant-phase";
import { CarryOnNotice, NeedsVeluxNotice, PillButton } from "./components";
import {
  answerForTranscript,
  assistantEscalationRecord,
  assistantNoteRecord,
  canSkipStep,
  escalationSkipOutcome,
} from "./escalation";
import { splitLinks } from "./linkify";

/**
 * The onboarding assistant — a help sheet that diagnoses a stuck installation.
 *
 * Design: docs/superpowers/specs/2026-07-27-onboarding-assistant-design.md
 *
 * Two entry points share this one sheet:
 *   - the `?` in the app bar, available on every screen
 *   - "stuck state" buttons on the screens where we know people get stuck,
 *     which open it with the problem pre-filled and auto-sent, so a stuck user
 *     doesn't have to describe what the app can already see
 *
 * The sheet itself is deliberately dumb: it sends where the user is (step +
 * stage) and the backend does the diagnosis, because only the backend can read
 * their hub. `available: false` in a reply means the model was unreachable and
 * the text is a support fallback, so we drop the assistant framing and show it
 * as a plain notice.
 *
 * Context is scoped per flow step ("phase"). Moving from one step to another
 * (e.g. windows to floor heating) starts a fresh conversation with fresh
 * suggested prompts, rather than carrying a resolved conversation about a
 * different problem into the new one. The old thread isn't discarded: it's
 * still there, collapsed, one tap away. See `assistant-phase.ts` for the rule.
 *
 * A reply can also say the problem is not the resident's to fix (`escalate`).
 * That is not a dead end and it is never a request to contact anyone: the
 * problem is written to the issue report VELUX reads, and the thread ends on a
 * card saying so. The chat stays open underneath, because "we will take it
 * from here" is a promise about this problem, not the end of the conversation.
 */

interface Turn {
  role: "user" | "assistant";
  content: string;
  /** False when this reply is a support fallback, not the model's words. */
  available?: boolean;
  /**
   * Object URL of a photo the user attached to this turn, so they can see what
   * they sent. Deliberately never revoked once a turn owns it: revoking would
   * blank an image that is still on screen, and these live only as long as the
   * sheet does.
   */
  photoUrl?: string;
  /**
   * True when the photo on this turn never reached us, so the answer below it
   * was written without ever seeing the image. Without this the thread shows
   * the photo next to a reply that ignored it, which reads as the assistant
   * looking at the picture and missing the obvious.
   */
  photoFailed?: boolean;
}

/**
 * An assistant answer, with any address in it made tappable.
 *
 * Only the assistant's words go through this. What the user typed is left
 * alone: they know what they wrote, and linking it back at them adds a
 * surface for nothing. See `linkify.ts` for which hosts are allowed and why.
 *
 * In practice that means links to a tutorial or a manufacturer's page: an
 * answer carrying an email address never gets this far, having been withheld
 * by `answerForTranscript` on the way in.
 */
function AnswerText({ text }: { text: string }) {
  return (
    <>
      {splitLinks(text).map((segment, i) => {
        if (segment.kind === "text") {
          return <Fragment key={i}>{segment.text}</Fragment>;
        }
        // A mail link hands off to the mail client, so a new tab would leave a
        // blank one behind. On a phone that is the whole screen.
        const isMail = segment.href.startsWith("mailto:");
        return (
          <a
            key={i}
            href={segment.href}
            target={isMail ? undefined : "_blank"}
            rel={isMail ? undefined : "noopener noreferrer"}
            className="underline underline-offset-2"
          >
            {segment.text}
          </a>
        );
      })}
    </>
  );
}

/**
 * One turn in the thread: a chat bubble, or the distinct "unavailable"
 * treatment for a support fallback. Shared between the active thread and a
 * collapsed prior phase's thread, so a windows conversation reads the same
 * whether it's the live thread or tucked away under floor heating.
 */
function TurnBubble({ turn }: { turn: Turn }) {
  if (turn.available === false) {
    return (
      <div
        role="status"
        className="border-border flex w-full items-start gap-3 rounded-2xl border bg-white px-4 py-3"
      >
        <WarningCircle weight="light" className="text-muted mt-0.5 size-5 shrink-0" />
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-muted type-label">ASSISTANT UNAVAILABLE</p>
          <p className="text-primary text-[15px] leading-[22px]">
            <AnswerText text={turn.content} />
          </p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-[22px]",
        turn.role === "user"
          ? "bg-accent text-accent-foreground self-end"
          : "bg-background text-primary self-start",
      )}
    >
      {turn.photoUrl && (
        <img
          src={turn.photoUrl}
          alt={turn.photoFailed ? "Photo that could not be sent" : "Photo you sent"}
          className={cn(
            "mb-2 max-h-48 w-full rounded-xl object-cover",
            turn.photoFailed && "opacity-50",
          )}
        />
      )}
      {turn.photoFailed && (
        <p className="type-caption mb-2 opacity-80">
          This photo didn&apos;t send, so the answer below doesn&apos;t use it. Try adding it again.
        </p>
      )}
      {turn.role === "user" ? turn.content : <AnswerText text={turn.content} />}
    </div>
  );
}

interface AssistantProviderProps {
  children: ReactNode;
  /**
   * The flow's own skip path, for the escalation card's way on. Reusing the
   * shell's `skip` rather than calling the endpoint from here is what keeps
   * one behaviour: the reason is recorded, the guide is where you land, and
   * the compartment shows as skipped rather than quietly unfinished.
   *
   * Omitted outside the flow (the dev gallery has no shell), which is also
   * what takes the CTA off the card.
   *
   * It answers with whether the household moved, and the sheet only closes on
   * true: a skip the shell declined or could not save leaves the card, the
   * conversation and a line saying so.
   */
  onSkipStep?: SkipStepHandler;
  /** True while that skip is in flight, so the CTA can't be double-tapped. */
  skipping?: boolean;
}

/**
 * Hosts the assistant sheet once for the whole flow and exposes `open()` to
 * descendants. Mounted by the flow shell, above the screen it renders, so a
 * single conversation persists as the user moves between screens.
 */
export function AssistantProvider({ children, onSkipStep, skipping }: AssistantProviderProps) {
  const [isOpen, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  // The escalation card's own error, kept apart from the chat's: it is about
  // the skip CTA rather than about an answer, and it belongs under the card
  // the tap was on.
  const [skipError, setSkipError] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [where, setWhere] = useState<OpenOptions>({});
  // The step key the active thread (turns/conversationId above) is scoped to.
  // Null until the first open. Compared against each open() call to decide
  // whether it's a new phase; see `isNewPhase`.
  const [phaseKey, setPhaseKey] = useState<string | null>(null);
  // The previous phase's turns, kept for continuity but shown collapsed. Only
  // ever holds the one thread the active phase most recently replaced: older
  // phases stay in the database (their own conversation row), just not on
  // screen, since the ticket asks for "the prior thread" collapsed, not a
  // stack of every phase visited this session.
  const [collapsedTurns, setCollapsedTurns] = useState<Turn[]>([]);
  const [phaseExpanded, setPhaseExpanded] = useState(false);
  // A captured photo waiting to be sent with the next message.
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null);
  // Covers the window `ask.isPending` misses: while the photo is uploading, no
  // mutation is in flight yet, so the send button would otherwise stay live and
  // a second tap would upload twice and ask the model twice.
  const [isSending, setSending] = useState(false);
  // Set by the first escalating reply of a thread and then left alone: one
  // card per conversation, carrying the answer that raised it as the reason a
  // skip would record. A second escalation is the same unsolved problem, so it
  // must not stack a second card or overwrite the first reason.
  const [escalation, setEscalation] = useState<{ reason: string } | null>(null);
  // Set by the first reply that carried a note for VELUX. Holds the screen's
  // own "carry on" text as of that moment: `where` is replaced on every
  // reopen, and the card must outlive that.
  const [carryOnNotice, setCarryOnNotice] = useState<string | null>(null);

  const ask = useAskAssistant();
  const recordIssue = useRecordIssue();
  // Conversations already written to the issue report. The backend upserts on
  // household + step + verdict so a duplicate write is harmless, but a thread
  // that escalates twice describes one problem, and the first answer is the
  // one worth keeping as its detail.
  const recordedConversations = useRef<Set<string>>(new Set());
  const recordedNotes = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  // Set when opened with a seed; consumed by the effect below so the send
  // happens after the sheet has mounted with its context.
  const pendingSeed = useRef<string | null>(null);
  // `isSending` drives the disabled button; this guards re-entrancy, because a
  // fast double tap can call send() twice before React has re-rendered.
  const sending = useRef(false);
  // Bumped on every phase change. A question asked just before the user moves
  // to a new step can resolve after that switch; without this, its answer
  // (and its conversation id) would land in the new, otherwise-unrelated
  // phase, which is the exact bug this fix exists to close. Kept as a ref
  // because `send()` needs a synchronous, always-current value; `activeEpoch`
  // below mirrors it into state purely so render can read it safely.
  const phaseEpoch = useRef(0);
  const [activeEpoch, setActiveEpoch] = useState(0);
  // Which epoch `sending.current` currently belongs to, so a still-settling
  // question from a phase the household has already left doesn't disable the
  // new phase's composer or show its loading dots under an unrelated
  // question. Same ref-plus-state-mirror split as phaseEpoch/activeEpoch.
  const pendingEpoch = useRef<number | null>(null);
  const [pendingEpochState, setPendingEpochState] = useState<number | null>(null);
  // Whether *this* phase has a question in flight, as opposed to a stale one
  // from a phase already left behind.
  const phasePending = isSending && pendingEpochState === activeEpoch;

  const capture = useCallback(async () => {
    const file = await pickImage();
    if (!file) {
      return; // the camera or picker was cancelled
    }
    const image = await downscaleToJpeg(file);
    setPhoto((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
      }
      return { file: image, url: URL.createObjectURL(image) };
    });
  }, []);

  const send = useCallback(
    async (question: string) => {
      // A photo on its own is a complete question, so supply the words the
      // backend requires rather than making the user type them.
      const trimmed = question.trim() || (photo ? "What is wrong with this?" : "");
      if (!trimmed) {
        return;
      }
      // Snapshot which phase this question belongs to. If the user moves to a
      // different step before the reply lands, phaseEpoch.current will have
      // moved on, and the result below is discarded instead of being written
      // into a phase it was never asked in.
      const epoch = phaseEpoch.current;
      // The screen this question was asked from names its own way forward.
      // Captured now, not when the reply lands: a reopen in the meantime
      // replaces `where`, and the card must describe the screen that asked.
      const carryOn = where.carryOn ?? null;
      // Re-entrancy is scoped to this phase: a fast double-tap on the same
      // question is blocked, but a question still settling in a phase the
      // household has already left must not freeze the new one.
      if (sending.current && pendingEpoch.current === epoch) {
        return;
      }
      pendingEpoch.current = epoch;
      setPendingEpochState(epoch);
      sending.current = true;
      setSending(true);
      setError("");
      setDraft("");
      setTurns((prev) => [...prev, { role: "user", content: trimmed, photoUrl: photo?.url }]);

      let photoId: string | undefined;
      if (photo) {
        try {
          const form = new FormData();
          form.append("photo", photo.file);
          const res = await fetch("/api/onboarding/assistant/photo", {
            method: "POST",
            credentials: "include",
            body: form,
          });
          if (!res.ok) {
            throw new Error(`assistant photo ${res.status}`);
          }
          photoId = ((await res.json()) as { photo_id: string }).photo_id;
        } catch (err) {
          // A failed upload must not swallow the question: send it text-only.
          // But say so, or the thread shows a photo beside an answer that never
          // saw it.
          logger.warn("[assistant] photo upload failed:", err);
          if (epoch === phaseEpoch.current) {
            setTurns((prev) =>
              prev.map((turn, i) =>
                i === prev.length - 1 ? { ...turn, photoFailed: true } : turn,
              ),
            );
          }
        }
        setPhoto(null);
      }

      try {
        const reply = await ask.mutateAsync({
          question: trimmed,
          step_key: where.stepKey ?? null,
          stage: where.stage ?? null,
          last_error: where.lastError ?? null,
          conversation_id: conversationId,
          photo_id: photoId ?? null,
        });
        if (!reply) {
          throw new Error("empty response");
        }
        if (epoch !== phaseEpoch.current) {
          // The household moved to a different step while this was in
          // flight. That step already got its own fresh thread; do not point
          // it at this answer's conversation or append an answer to a
          // question it never showed.
          return;
        }
        setConversationId(reply.conversation_id);
        // The homeowner is deliberately shown one generic fallback whatever went
        // wrong, which leaves nothing to debug from. Log the operator-facing
        // reason so a misconfigured environment (no OPENAI_API_KEY, most often)
        // is one console line instead of an investigation.
        if (reply.available === false) {
          logger.warn("[assistant] degraded response, reason:", reply.reason ?? "unknown");
        }
        // A correction the resident carries on from: recorded for VELUX,
        // shown as an ordinary answer, no escalation card and no skip offer.
        // Only without an escalation: the backend already drops the note in
        // that case, and a turn that both stops and carries on is a contradiction.
        if (reply.note_for_velux && reply.escalate !== true) {
          const key = `${reply.conversation_id}:${reply.note_for_velux}`;
          if (!recordedNotes.current.has(key)) {
            recordedNotes.current.add(key);
            recordIssue.mutate(
              assistantNoteRecord({
                stepKey: where.stepKey,
                note: reply.note_for_velux,
                conversationId: reply.conversation_id,
              }),
              {
                onError: (err) => logger.warn("[assistant] could not record the note", err),
              },
            );
          }
          if (carryOn) {
            setCarryOnNotice((current) => current ?? carryOn);
          }
        }
        const escalating = reply.escalate === true;
        if (escalating) {
          // Record before rendering: the card promises the problem has been
          // noted, so the write that makes that true is the first thing to
          // happen. Fire-and-forget, because a failed write must not swallow
          // the answer the resident is waiting for; the warning is what tells
          // an operator the report is missing a row.
          if (!recordedConversations.current.has(reply.conversation_id)) {
            recordedConversations.current.add(reply.conversation_id);
            recordIssue.mutate(
              assistantEscalationRecord({
                stepKey: where.stepKey,
                answer: reply.answer,
                conversationId: reply.conversation_id,
              }),
              {
                onError: (err) =>
                  logger.warn("[assistant] could not record the escalated issue", err),
              },
            );
          }
          setEscalation((current) => current ?? { reason: reply.answer });
        }
        // Null when the card is about to say the same thing; see
        // `answerForTranscript` for the address rule behind it.
        const shown = answerForTranscript(reply.answer, escalating);
        if (shown !== null) {
          setTurns((prev) => [
            ...prev,
            { role: "assistant", content: shown, available: reply.available },
          ]);
        }
      } catch (err) {
        logger.warn("[assistant] ask failed:", err);
        if (epoch === phaseEpoch.current) {
          setError(getApiErrorMessage(err, "We couldn't reach the assistant. Please try again."));
        }
      } finally {
        // Only clear the shared sending flags if this question is still the
        // one they refer to. If a newer phase has already started its own
        // question, this stale one finishing must not stop that one's
        // spinner or silently re-enable it for a second send.
        if (pendingEpoch.current === epoch) {
          pendingEpoch.current = null;
          setPendingEpochState(null);
          sending.current = false;
          setSending(false);
        }
      }
    },
    [ask, recordIssue, conversationId, where, photo],
  );

  const open = useCallback(
    (options: OpenOptions = {}) => {
      if (isNewPhase(phaseKey, options.stepKey)) {
        // A different step is a different question. Tuck the old thread away
        // instead of showing it under the new one, and start a fresh
        // conversation so the model isn't fed a problem the household has
        // already moved past. Nothing is deleted: the old thread is still
        // there, collapsed, and its messages stay in the database under their
        // own conversation id.
        //
        // Only replace the collapsed thread if this phase actually said
        // something: an empty phase (opened, nothing asked, moved on again)
        // must not overwrite a real prior thread with nothing.
        if (turns.length > 0) {
          setCollapsedTurns(turns);
        }
        setTurns([]);
        setConversationId(null);
        setPhaseExpanded(false);
        // The card belongs to the thread that raised it. A new step is a new
        // problem, and carrying "this one needs VELUX" into it would promise
        // something about a compartment nobody has looked at yet.
        setEscalation(null);
        setCarryOnNotice(null);
        setSkipError("");
        // A planted draft belongs to the step that planted it. Carrying "I
        // can't tell which socket on my Nilan" into the windows step would
        // send a canned sentence against the wrong problem, and a photo of
        // one compartment says nothing about the next.
        setDraft("");
        setPhoto((current) => {
          if (current) {
            URL.revokeObjectURL(current.url);
          }
          return null;
        });
        setPhaseKey(options.stepKey ?? null);
        phaseEpoch.current += 1;
        setActiveEpoch(phaseEpoch.current);
      }
      setWhere(options);
      setError("");
      // A failed skip is news about one tap. Reopening the sheet is a fresh
      // go at it, so the line does not survive into it.
      setSkipError("");
      setOpen(true);
      if (options.seed) {
        pendingSeed.current = options.seed;
      }
      // Never over text the resident has already typed: a second tap of the
      // link that planted the draft must not wipe their edit. After a send the
      // composer is empty, so the canned sentence still returns when it should.
      const draft = options.draft;
      if (draft) {
        setDraft((current) => (current.trim() ? current : draft));
      }
    },
    [phaseKey, turns],
  );

  // Fire a seeded question once the sheet is open and `where` has been applied,
  // so the backend gathers state for the right step.
  useEffect(() => {
    if (!isOpen || !pendingSeed.current) {
      return;
    }
    const seed = pendingSeed.current;
    pendingSeed.current = null;
    void send(seed);
  }, [isOpen, send]);

  // Keep the newest turn in view as the conversation grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, phasePending, escalation]);

  /**
   * Leave the escalated step behind and get on with the rest.
   *
   * Only offered where the flow has a skip to give: the hub, the network and
   * the welcome step gate everything after them, so skipping one would move
   * the dead end a screen later rather than clearing it. Those keep the card
   * without the button, which still leaves the promise and the open chat.
   */
  const skipEscalatedStep = useCallback(async () => {
    const stepKey = where.stepKey;
    if (!onSkipStep || !escalation || !stepKey || !canSkipStep(stepKey)) {
      return;
    }
    logger.debug("[assistant] skipping the escalated step", stepKey);
    // The shell's own skip: it records the reason, marks the compartment
    // skipped and lands on the guide. Awaited so the sheet closes onto the
    // guide rather than onto a frame of the screen being left behind, and its
    // answer is read rather than assumed: the shell declines a skip that is
    // already in flight, and a failed one leaves its error on the screen
    // behind this sheet, where nobody can see it.
    const skipped = await onSkipStep(stepKey, escalation.reason);
    const outcome = escalationSkipOutcome(skipped);
    setSkipError(outcome.error);
    if (outcome.close) {
      setOpen(false);
    }
  }, [onSkipStep, escalation, where.stepKey]);

  const canLeaveStep = onSkipStep != null && canSkipStep(where.stepKey);

  return (
    <AssistantContext.Provider value={{ open }}>
      {children}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex justify-center">
          <button
            type="button"
            aria-label="Close assistant"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/25"
          />
          <div className="relative mt-auto flex h-[85vh] w-full max-w-[390px] flex-col rounded-t-3xl bg-white shadow-[0_-8px_32px_rgba(40,35,31,0.18)]">
            <div className="border-border-subtle flex items-center gap-2 border-b px-5 py-4">
              <Sparkle weight="light" className="text-accent size-5" />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="text-primary type-subhead">Setup help</p>
                <p className="text-muted type-body-sm">
                  I can see how your system is doing right now
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="bg-surface-subtle grid size-9 shrink-0 place-items-center rounded-full"
              >
                <X weight="light" className="text-primary size-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
              {collapsedTurns.length > 0 && (
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => setPhaseExpanded((prev) => !prev)}
                    aria-expanded={phaseExpanded}
                    className="border-border-subtle text-muted flex w-full items-center justify-between rounded-xl border border-dashed px-3 py-2 text-left text-[13px]"
                  >
                    <span>Earlier conversation</span>
                    <ChevronDown
                      className={cn(
                        "size-4 shrink-0 transition-transform",
                        phaseExpanded && "rotate-180",
                      )}
                    />
                  </button>
                  {phaseExpanded && (
                    <div className="flex flex-col gap-3 opacity-70">
                      {collapsedTurns.map((turn, i) => (
                        <TurnBubble key={i} turn={turn} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {turns.length === 0 && !phasePending && (
                <div className="flex flex-col gap-3">
                  <p className="text-muted text-[17px] leading-6">
                    Ask me anything about your setup. I&apos;ll check what your system is reporting
                    before I answer.
                  </p>
                  <div className="flex flex-col gap-2">
                    {suggestionsForStep(where.stepKey).map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => void send(suggestion)}
                        className="border-border hover:border-accent text-primary rounded-2xl border bg-white px-4 py-3 text-left text-[15px] leading-[22px] transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {turns.map((turn, i) => (
                <TurnBubble key={i} turn={turn} />
              ))}

              {phasePending && (
                <div
                  className="bg-background flex items-center gap-1.5 self-start rounded-2xl px-4 py-4"
                  aria-label="Checking your system"
                >
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className="bg-faint size-2 animate-pulse rounded-full"
                      style={{ animationDelay: `${dot * 150}ms` }}
                    />
                  ))}
                </div>
              )}

              {/* Pinned under the thread rather than sent as a turn: it is the
                  app speaking, not the assistant, and it has to stay the last
                  word in the conversation however many questions follow it.
                  One per thread, so a second escalated answer adds nothing. */}
              {carryOnNotice && !escalation && <CarryOnNotice carryOn={carryOnNotice} />}
              {escalation && (
                <div className="flex w-full flex-col gap-2">
                  <NeedsVeluxNotice>
                    {canLeaveStep && (
                      <PillButton
                        label="Continue with next step"
                        busy={skipping}
                        onClick={() => void skipEscalatedStep()}
                      />
                    )}
                  </NeedsVeluxNotice>
                  {skipError && (
                    <p role="alert" className="text-destructive type-body-sm">
                      {skipError}
                    </p>
                  )}
                </div>
              )}

              {error && (
                <p role="alert" className="text-destructive type-body-sm self-start">
                  {error}
                </p>
              )}
            </div>

            {photo && (
              <div className="border-border-subtle flex items-center gap-3 border-t px-5 pt-4">
                <img
                  src={photo.url}
                  alt="Photo you are about to send"
                  className="size-14 rounded-xl object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    URL.revokeObjectURL(photo.url);
                    setPhoto(null);
                  }}
                  className="text-muted type-caption focus-visible:outline-ring underline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  Remove photo
                </button>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send(draft);
              }}
              className={cn(
                "flex items-center gap-2 px-5 py-4",
                !photo && "border-border-subtle border-t",
              )}
            >
              <button
                type="button"
                onClick={() => void capture()}
                aria-label="Add a photo"
                className="border-border text-muted hover:border-accent focus-visible:outline-ring grid size-11 shrink-0 place-items-center rounded-full border bg-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <Camera weight="light" className="size-5" />
              </button>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask about your setup…"
                aria-label="Ask about your setup"
                className="border-border focus:border-ring text-primary placeholder:text-muted h-11 min-w-0 flex-1 rounded-md border bg-white px-4 text-[15px] outline-none"
              />
              <button
                type="submit"
                disabled={(!draft.trim() && !photo) || phasePending}
                aria-label="Send"
                className="bg-accent active:bg-accent-pressed grid size-11 shrink-0 place-items-center rounded-full disabled:opacity-40"
              >
                <ArrowUp weight="light" className="size-5 text-white" />
              </button>
            </form>
          </div>
        </div>
      )}
    </AssistantContext.Provider>
  );
}
